import { Gauge, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis } from "recharts";

interface PressureTrendProps {
  hourly: { dt: number; pressure: number }[];
  currentPressure: number;
}

export function PressureTrend({ hourly, currentPressure }: PressureTrendProps) {
  const data = hourly.slice(0, 12).map((h) => ({ pressure: h.pressure }));
  const start = data[0]?.pressure || currentPressure;
  const end = data[data.length - 1]?.pressure || currentPressure;
  const delta = end - start;
  const trend = Math.abs(delta) < 1 ? "steady" : delta > 0 ? "rising" : "falling";

  const trendIcon = trend === "rising" ? TrendingUp : trend === "falling" ? TrendingDown : Minus;
  const TrendIcon = trendIcon;
  const trendColor = trend === "rising" ? "oklch(0.7 0.18 145)" : trend === "falling" ? "oklch(0.7 0.18 30)" : "oklch(0.6 0.05 240)";

  const interpretation = trend === "rising"
    ? "Improving conditions"
    : trend === "falling"
    ? "Storm potential"
    : "Stable conditions";

  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold flex items-center gap-2">
        <Gauge className="h-4 w-4 text-primary" />
        Pressure Trend
      </h3>

      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold font-mono">{currentPressure}</span>
        <span className="text-xs text-muted-foreground">hPa</span>
        <div className="ml-auto flex items-center gap-1 text-xs" style={{ color: trendColor }}>
          <TrendIcon className="h-3.5 w-3.5" />
          <span className="font-semibold capitalize">{trend}</span>
          {Math.abs(delta) >= 1 && <span className="font-mono">({delta > 0 ? "+" : ""}{delta.toFixed(0)})</span>}
        </div>
      </div>

      <div className="h-12 mt-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <YAxis hide domain={["dataMin - 2", "dataMax + 2"]} />
            <Line
              type="monotone"
              dataKey="pressure"
              stroke={trendColor}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="text-[11px] text-muted-foreground mt-1">{interpretation}</p>
    </div>
  );
}
